import { useState, useEffect, useRef, useCallback } from 'react';
import { trackEvent } from '../utils/analytics';

interface UsePracticeSessionProps {
  initialPractice: any;
  onComplete: (correctCount: number, incorrectCount: number) => void;
  onUpdateResults: (practiceId: number, isCorrect: boolean) => void;
}

// interface UsePracticeSessionState {
//   currentPractice: any;
//   currentWord: string;
//   userInput: string[];
//   activeInputIndex: number;
//   feedback: {
//     visible: boolean;
//     correct: boolean;
//     correctWord: string;
//     attemptCount: number;
//   };
//   usedWords: string[];
//   correctCount: number;
//   incorrectCount: number;
//   showKeyboard: boolean;
//   isPronouncing: boolean;
//   pronunciationStatus: 'idle' | 'loading' | 'success' | 'error';
// }

const usePracticeSession = ({
  initialPractice,
  onComplete,
  onUpdateResults,
}: UsePracticeSessionProps) => {
  const [currentPractice, setCurrentPractice] = useState(initialPractice);
  const [currentWord, setCurrentWord] = useState('');
  const [userInput, setUserInput] = useState<string[]>([]);
  const [activeInputIndex, setActiveInputIndex] = useState(0);
  const [feedback, setFeedback] = useState({
    visible: false,
    correct: false,
    correctWord: '',
    attemptCount: 0
  });
  const [usedWords, setUsedWords] = useState<string[]>([]);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  // Always show keyboard by default in practice view
  const [showKeyboard, setShowKeyboard] = useState(true);
  const [isPronouncing, setIsPronouncing] = useState(false);
  const [pronunciationStatus, setPronunciationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  // Use a more specific type for the refs to avoid TypeScript errors
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Use useCallback to memoize the initializeSession function
  const initializeSession = useCallback(() => {
    if (initialPractice && initialPractice.words && initialPractice.words.length > 0) {
      setCurrentPractice(initialPractice);
      // Instead of calling pickRandomWord directly, we'll set up the initial state
      const availableWords = initialPractice.words;
      if (availableWords.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableWords.length);
        const word = availableWords[randomIndex];
        
        setCurrentWord(word);
        setUsedWords([word]);
        setUserInput(Array(word.length).fill(''));
        setActiveInputIndex(0);
        setFeedback({ visible: false, correct: false, correctWord: '', attemptCount: 0 });
        
        // Reset other state
        setCorrectCount(0);
        setIncorrectCount(0);
        // Always keep keyboard visible
        setShowKeyboard(true);
        
        // Initialize input refs
        inputRefs.current = Array(word.length).fill(null);
        
        // Focus the first input after a short delay
        setTimeout(() => {
          // Use optional chaining for safer access
          inputRefs.current[0]?.focus();
        }, 100);
      }
    }
  }, [initialPractice]);
  
  // Only run this effect when initialPractice changes
  useEffect(() => {
    initializeSession();
  }, [initialPractice, initializeSession]);
  
  // Also reinitialize when the component is mounted
  useEffect(() => {
    // This will reset the session when the component is mounted
    initializeSession();
  }, [initializeSession]);

  // Memoize pickRandomWord to prevent it from being recreated on every render
  // This is crucial to prevent infinite loops
  const pickRandomWord = () => {
    // Use the current practice's words and the current usedWords state
    if (!currentPractice || !currentPractice.words) return;
    
    const wordList = currentPractice.words;
    // Add explicit type annotation to fix the TypeScript error
    const availableWords = wordList.filter((word: string) => !usedWords.includes(word));

    if (availableWords.length === 0) {
      setShowKeyboard(true);
      
      // Track practice completion event
      trackEvent('practice_completed', {
        correct_count: correctCount,
        incorrect_count: incorrectCount,
        total_words: usedWords.length,
        accuracy: Math.round((correctCount / (correctCount + incorrectCount)) * 100)
      });
      
      onComplete && onComplete(correctCount, incorrectCount);
      return;
    }

    const randomIndex = Math.floor(Math.random() * availableWords.length);
    const word = availableWords[randomIndex];

    // Use functional updates to prevent stale state references
    setCurrentWord(word);
    setUsedWords(prev => [...prev, word]);
    setUserInput(Array(word.length).fill(''));
    setActiveInputIndex(0);
    setFeedback({ visible: false, correct: false, correctWord: '', attemptCount: 0 });

    // Update refs directly
    inputRefs.current = Array(word.length).fill(null);

    // Use setTimeout for any async operations
    setTimeout(() => {
      // Use optional chaining for safer access
      inputRefs.current[0]?.focus();
    }, 100);
  };

  const pronounceWord = async () => {
    if (!currentWord) return;

    setIsPronouncing(true);
    setPronunciationStatus('loading');

    try {
      const utterance = new SpeechSynthesisUtterance(currentWord);
      window.speechSynthesis.speak(utterance);
      setPronunciationStatus('success');
    } catch (error) {
      console.error('Error pronouncing word:', error);
      setPronunciationStatus('error');
    } finally {
      setIsPronouncing(false);
    }
  };

  const checkAnswer = (inputArray = userInput) => {
    if (feedback.visible) return;

    // Create a version of the word and input that ignores spaces
    const wordWithoutSpaces = currentWord.replace(/ /g, '');
    
    // Create a new array with only the inputs for non-space characters
    const inputWithoutSpaces: string[] = [];
    let nonSpaceIndex = 0;
    
    for (let i = 0; i < currentWord.length; i++) {
      if (currentWord[i] !== ' ') {
        inputWithoutSpaces[nonSpaceIndex] = inputArray[i] || '';
        nonSpaceIndex++;
      }
    }
    
    // Check if the input matches the word (ignoring spaces)
    const isCorrect = inputWithoutSpaces.join('').toLowerCase() === wordWithoutSpaces.toLowerCase();

    // Show feedback
    if (isCorrect) {
      setFeedback({
        visible: true,
        correct: true,
        correctWord: currentWord,
        attemptCount: feedback.attemptCount
      });

      // Only update counts if it's the first attempt
      if (feedback.attemptCount === 0) {
        setCorrectCount(correctCount + 1);
        onUpdateResults && onUpdateResults(currentPractice.id, true);
      }

      // Move to next word after delay
      setTimeout(() => {
        setFeedback({ visible: false, correct: false, correctWord: '', attemptCount: 0 });
        pickRandomWord();
      }, 2000);
    } else {
      // First attempt starts at 1, subsequent attempts increment from there
      const newAttemptCount = feedback.attemptCount + 1;
      
      // Show feedback
      setFeedback({
        visible: true,
        correct: false,
        correctWord: currentWord,
        attemptCount: newAttemptCount
      });

      // Only count as incorrect on first attempt
      if (feedback.attemptCount === 0) {
        setIncorrectCount(incorrectCount + 1);
        onUpdateResults && onUpdateResults(currentPractice.id, false);
      }

      // Clear input after a delay and let them try again
      setTimeout(() => {
        setUserInput(Array(currentWord.length).fill(''));
        setActiveInputIndex(0);
        setFeedback(prev => ({ 
          visible: false, 
          correct: false, 
          correctWord: '', 
          attemptCount: newAttemptCount // Keep track of the attempt count
        }));
        // Focus the first input
        inputRefs.current[0]?.focus();
      }, 2000);
    }
  };
  
  // Add a skip word function that counts as incorrect and moves to the next word
  const skipWord = () => {
    if (feedback.visible) return;
    
    // Show the correct word briefly before moving on
    setFeedback({
      visible: true,
      correct: false,
      correctWord: currentWord,
      attemptCount: 0
    });
    
    // Count as incorrect
    setIncorrectCount(prevCount => prevCount + 1);
    onUpdateResults && onUpdateResults(currentPractice.id, false);
    
    // Move to next word after a brief delay
    setTimeout(() => {
      setFeedback({ visible: false, correct: false, correctWord: '', attemptCount: 0 });
      pickRandomWord();
    }, 2000);
  };

  const handleInputChange = (index: number, value: string) => {
    if (value && !/^[a-zA-Z]$/.test(value)) {
      return;
    }

    const newInput = [...userInput];
    newInput[index] = value.toLowerCase();
    setUserInput(newInput);

    if (value) {
      // Find the next non-space index
      let nextIndex = index + 1;
      while (nextIndex < currentWord.length && currentWord[nextIndex] === ' ') {
        nextIndex++;
      }
      
      if (nextIndex < currentWord.length) {
        setActiveInputIndex(nextIndex);
        // Use optional chaining for safer access
        inputRefs.current[nextIndex]?.focus();
      } else {
        checkAnswer(newInput);
      }
    }
  };

  const handleKeyDown = (event: any, index: number) => {
    if (event.key === 'Backspace') {
      if (userInput[index] === '') {
        // Find the previous non-space index
        let prevIndex = index - 1;
        while (prevIndex >= 0 && currentWord[prevIndex] === ' ') {
          prevIndex--;
        }
        
        if (prevIndex >= 0) {
          event.preventDefault();
          setActiveInputIndex(prevIndex);
          // Use optional chaining for safer access
          inputRefs.current[prevIndex]?.focus();
        }
      } else {
        const newInput = [...userInput];
        newInput[index] = '';
        setUserInput(newInput);
      }
    } else if (event.key === 'ArrowLeft') {
      // Find the previous non-space index
      let prevIndex = index - 1;
      while (prevIndex >= 0 && currentWord[prevIndex] === ' ') {
        prevIndex--;
      }
      
      if (prevIndex >= 0) {
        event.preventDefault();
        setActiveInputIndex(prevIndex);
        // Use optional chaining for safer access
        inputRefs.current[prevIndex]?.focus();
      }
    } else if (event.key === 'ArrowRight') {
      // Find the next non-space index
      let nextIndex = index + 1;
      while (nextIndex < currentWord.length && currentWord[nextIndex] === ' ') {
        nextIndex++;
      }
      
      if (nextIndex < currentWord.length) {
        event.preventDefault();
        setActiveInputIndex(nextIndex);
        // Use optional chaining for safer access
        inputRefs.current[nextIndex]?.focus();
      }
    }
  };

  const handleInputClick = (index: number) => {
    if (!feedback.visible) {
      setActiveInputIndex(index);
    }
  };

  const handleKeyPress = (key: string) => {
    if (feedback.visible) return;

    if (activeInputIndex === null || activeInputIndex >= currentWord.length) {
      for (let i = 0; i < currentWord.length; i++) {
        if (!userInput[i] && currentWord[i] !== ' ') {
          setActiveInputIndex(i);
          // Use optional chaining to safely access the input element
          inputRefs.current[i]?.focus();
          break;
        }
      }
      return;
    }

    if (key === 'backspace') {
      if (userInput[activeInputIndex]) {
        const newInput = [...userInput];
        newInput[activeInputIndex] = '';
        setUserInput(newInput);
      } else if (activeInputIndex > 0) {
        // Find the previous non-space index
        let prevIndex = activeInputIndex - 1;
        while (prevIndex >= 0 && currentWord[prevIndex] === ' ') {
          prevIndex--;
        }
        
        if (prevIndex >= 0) {
          setActiveInputIndex(prevIndex);
          // Use optional chaining for safer access
          inputRefs.current[prevIndex]?.focus();
        }
      }
    } else {
      const newInput = [...userInput];
      newInput[activeInputIndex] = key;
      setUserInput(newInput);

      // Find the next non-space index
      let nextIndex = activeInputIndex + 1;
      while (nextIndex < currentWord.length && currentWord[nextIndex] === ' ') {
        nextIndex++;
      }
      
      if (nextIndex < currentWord.length) {
        setActiveInputIndex(nextIndex);
        // Use optional chaining for safer access
        inputRefs.current[nextIndex]?.focus();
      } else {
        // Check if all non-space characters have been filled
        const isComplete = currentWord.split('').every((char, idx) => {
          // If it's a space, we don't need input
          if (char === ' ') return true;
          // Otherwise, check if there's input for this character
          return newInput[idx] && newInput[idx].trim() !== '';
        });
        
        if (isComplete) {
          checkAnswer(newInput);
        }
      }
    }
  };

  return {
    currentPractice,
    currentWord,
    userInput,
    activeInputIndex,
    feedback,
    inputRefs,
    isPronouncing,
    pronunciationStatus,
    correctCount,
    incorrectCount,
    showKeyboard,
    usedWords,
    pronounceWord,
    checkAnswer,
    skipWord,
    handleInputChange,
    handleKeyDown,
    handleInputClick,
    handleKeyPress,
    setShowKeyboard,
    pickRandomWord,
  };
};

export default usePracticeSession;
